/**
 * Referral code derivation.
 *
 * A referral code is a pure function of `(userId, targetCourseId)` — no
 * Firestore read, no stored counter. That is what makes it usable from
 * anywhere: the application UI derives it to show a share link, and the email
 * pipeline derives it server-side to put each recipient's own link in a
 * templated send.
 *
 * The code doubles as the document id of the referral record at
 * `applicationReferrals/{targetCourseId}/hashes/{code}` — see
 * `getPerHashReferralDataDocPath` in `../../firebase`.
 *
 * Deriving a code does **not** register it. The record's `referrerId` — the
 * only thing tying a code back to a person for the leaderboard — is written
 * separately, historically by the share modal at the moment a user opens it.
 * Anything that hands a user their code without going through that modal has
 * to register it too, or referrals through that code land on an ownerless
 * record.
 *
 * @module
 */

import {hashStringTo32BitSha256} from '../../hash';
import type {CourseId, UserId} from '../../types';

/**
 * Characters a referral code is drawn from: digits and consonants only.
 *
 * Vowels are excluded so a generated code cannot spell an offensive word —
 * these codes are shown to users and shared publicly.
 */
const CHARSET = '0123456789bcdfghjklmnpqrstvwxyz';

/** Length of a generated code. 31^6 ≈ 887 million distinct codes. */
const CODE_LENGTH = 6;

/**
 * Derives a user's referral code for a course.
 *
 * Deterministic: the same pair always yields the same code, which is what
 * lets a code be derived on demand rather than stored and looked up.
 *
 * Note that `cip5` and `pylatino-25` predate this derivation and used Java's
 * `String.hashCode`; their existing codes are not reproducible here. Only use
 * this for courses whose codes were minted by this function.
 *
 * @param userId - Firebase Auth uid of the referrer.
 * @param targetCourseId - The course being applied *to*, not the course
 *   hosting the application. A user has a different code per target course.
 * @return A six-character lowercase code.
 * @throws Never.
 */
export function getReferralCode(
  userId: UserId,
  targetCourseId: CourseId,
): string {
  const hash = hashStringTo32BitSha256(`${userId}-${targetCourseId}`);
  let code = '';
  let remaining = hash;

  for (let i = 0; i < CODE_LENGTH; i++) {
    code = CHARSET[remaining % CHARSET.length] + code;
    remaining = Math.floor(remaining / CHARSET.length);
  }

  return code;
}

/**
 * Query parameter carrying a referral code on an application link.
 *
 * Read by the application's referral hook, which records the usage against
 * the code's record.
 */
export const REFERRAL_CODE_QUERY_PARAM = 'r' as const;

/**
 * Appends a referral code to an application link.
 *
 * @param params - Link parameters.
 * @param params.linkBase - Absolute URL of the application's splash page. The
 *   route shape differs per course (long form vs the `/apply/…` short link),
 *   so callers build it with the app's own route helpers rather than having
 *   this module reimplement them. An existing query string is preserved.
 * @param params.referralCode - The code to attach.
 * @return The link with the referral parameter set.
 * @throws Never.
 */
export function buildReferralLink({
  linkBase,
  referralCode,
}: {
  linkBase: string;
  referralCode: string;
}): string {
  const separator = linkBase.includes('?') ? '&' : '?';
  return `${linkBase}${separator}${REFERRAL_CODE_QUERY_PARAM}=${encodeURIComponent(referralCode)}`;
}

/**
 * Copies the referral code from a query string onto another link.
 *
 * The application credits a code only while it is still in the page's URL, so
 * anything that builds a fresh link on the way there — an apply button, the
 * continue URL of a sign-in email — has to carry the code across or the
 * referral is silently lost. See
 * [#4467](https://github.com/CodeinPlace/cip/issues/4467).
 *
 * Only the referral parameter is carried. Everything else in `search` — UTM
 * tags, click ids, a sign-in link's one-time code — is deliberately left behind.
 *
 * @param params - Link parameters.
 * @param params.link - The destination, as a path or absolute URL, with or
 *   without its own query string. Must not contain a `#` fragment.
 * @param params.search - The query string to read the code from, typically
 *   `location.search`, with or without its leading `?`.
 * @return `link` with the referral code appended. `link` unchanged when
 *   `search` carries no code (absent or empty), or when `link` already carries
 *   one of its own.
 * @throws Never.
 */
export function carryReferralCode({
  link,
  search,
}: {
  link: string;
  search: string;
}): string {
  const referralCode = new URLSearchParams(search).get(
    REFERRAL_CODE_QUERY_PARAM,
  );
  if (!referralCode) {
    return link;
  }
  const queryStart = link.indexOf('?');
  const linkQuery = queryStart === -1 ? '' : link.slice(queryStart);
  if (new URLSearchParams(linkQuery).has(REFERRAL_CODE_QUERY_PARAM)) {
    return link;
  }
  return buildReferralLink({linkBase: link, referralCode});
}
